import { PRESET_SCHEDULES } from "@netkeep/fee-presets";
import type { FeeSchedule } from "@netkeep/fee-types";
import { type ReactNode, createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { fetchLatestSchedules } from "@/lib/api";
import { DEFAULT_API_URL, DEFAULT_REGION } from "@/lib/config";
import { KEYS, readJson, removeKey, writeJson } from "@/lib/storage";

/** Where the schedules currently in use came from. */
export type FeeDataOrigin = "bundled" | "cache" | "network";

interface ScheduleCache {
  fetchedAt: string;
  asOf: string;
  items: FeeSchedule[];
}

interface FeeDataState {
  schedules: FeeSchedule[];
  origin: FeeDataOrigin;
  /** When the schedules were last downloaded (null for bundled data). */
  fetchedAt: string | null;
  refreshing: boolean;
  lastError: string | null;
  refresh: () => Promise<void>;
}

const FeeDataContext = createContext<FeeDataState | null>(null);

/** The last cached server response, if it looks usable. */
function readCache(): ScheduleCache | null {
  const cache = readJson<ScheduleCache>(KEYS.scheduleCache);
  return cache && Array.isArray(cache.items) && cache.items.length > 0 ? cache : null;
}

/**
 * Supplies fee schedules with an offline-first strategy (same as the mobile app):
 *   1. Start with schedules bundled in the app (always available).
 *   2. Replace them with the last cached server response, if any.
 *   3. Try the server; on success cache and use; on failure keep what we have.
 *
 * The server is always DEFAULT_API_URL (VITE_API_URL at build time). Users cannot change it.
 */
export function FeeDataProvider({ children }: { children: ReactNode }) {
  // localStorage is synchronous, so the cache applies before the first render.
  const [initial] = useState(readCache);
  const [schedules, setSchedules] = useState<FeeSchedule[]>(initial?.items ?? PRESET_SCHEDULES);
  const [origin, setOrigin] = useState<FeeDataOrigin>(initial ? "cache" : "bundled");
  const [fetchedAt, setFetchedAt] = useState<string | null>(initial?.fetchedAt ?? null);
  const [refreshing, setRefreshing] = useState(false);
  const [lastError, setLastError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setRefreshing(true);
    try {
      const latest = await fetchLatestSchedules(DEFAULT_API_URL, DEFAULT_REGION);
      const cache: ScheduleCache = { fetchedAt: new Date().toISOString(), asOf: latest.asOf, items: latest.items };
      writeJson(KEYS.scheduleCache, cache);
      setSchedules(latest.items);
      setOrigin("network");
      setFetchedAt(cache.fetchedAt);
      setLastError(latest.rejected > 0 ? `${latest.rejected} malformed schedule(s) were ignored.` : null);
    } catch (err) {
      setLastError(err instanceof Error ? err.message : String(err));
    } finally {
      setRefreshing(false);
    }
  }, []);

  // Fetch once on load. Older versions let users pick a server in Settings; forget any address saved then.
  useEffect(() => {
    removeKey(KEYS.legacyApiUrl);
    void refresh();
  }, [refresh]);

  const value = useMemo(
    () => ({ schedules, origin, fetchedAt, refreshing, lastError, refresh }),
    [schedules, origin, fetchedAt, refreshing, lastError, refresh],
  );
  return <FeeDataContext.Provider value={value}>{children}</FeeDataContext.Provider>;
}

export function useFeeData(): FeeDataState {
  const ctx = useContext(FeeDataContext);
  if (!ctx) throw new Error("useFeeData must be used inside FeeDataProvider");
  return ctx;
}
